import React, { useState, useEffect, useCallback } from 'react';
import { Mail, Plus, Trash2, Save, X, Eye, Loader2, Upload, Download, History, RotateCcw, Code, Layout, AlertCircle, Copy, Check, Image } from 'lucide-react';
import api from '../lib/api';
import Button from '../components/Button';
import SearchField from '../components/SearchField';
import ConfirmationDialog from '../components/ConfirmationDialog';
import { usePageTitle } from '../hooks/usePageTitle';
import toast from 'react-hot-toast';
import Tabs from '@shared/ui/Tabs';
import Modal from '@shared/ui/Modal';
import { EditIcon } from '../components/icons';
import ActionsHeaderHelp from '../components/ActionsHeaderHelp';
import {
  StandardTable,
  StandardTableHeader,
  StandardTableHeaderCell,
  StandardTableBody,
  StandardTableRow,
  StandardTableCell
} from '../components/StandardTable';

// Types
interface EmailTemplate {
  id: string;
  name: string;
  subject: string;
  htmlContent: string;
  useBaseLayout: boolean;
  placeholders: string[];
  sampleData: Record<string, string>;
}

interface VersionInfo {
  id: string;
  key: string;
  timestamp: string;
  size?: number;
}

interface Partials {
  baseLayout: string;
  partials: {
    header: string;
    footer: string;
  };
}

const EmailTemplatesAdminPage: React.FC = () => {
  usePageTitle('Email Templates', 'Manage outbound email templates');

  // Tab state
  const [activeTab, setActiveTab] = useState<'templates' | 'layout' | 'versions'>('templates');

  // Templates state
  const [templates, setTemplates] = useState<EmailTemplate[]>([]);
  const [templatesLoading, setTemplatesLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');

  // Partials state
  const [partials, setPartials] = useState<Partials | null>(null);
  const [partialsLoading, setPartialsLoading] = useState(false);

  // Versions state
  const [versions, setVersions] = useState<VersionInfo[]>([]);
  const [versionsLoading, setVersionsLoading] = useState(false);

  // Editor state
  const [isEditorOpen, setIsEditorOpen] = useState(false);
  const [editorMode, setEditorMode] = useState<'create' | 'edit'>('create');
  const [editingTemplate, setEditingTemplate] = useState<EmailTemplate | null>(null);
  const [saving, setSaving] = useState(false);

  // Template form state
  const [formData, setFormData] = useState({
    id: '',
    name: '',
    subject: '',
    htmlContent: '',
    useBaseLayout: true,
    placeholders: [] as string[],
    sampleData: {} as Record<string, string>
  });
  const [placeholderInput, setPlaceholderInput] = useState('');

  // Preview state
  const [showPreview, setShowPreview] = useState(false);
  const [previewHtml, setPreviewHtml] = useState('');
  const [previewSubject, setPreviewSubject] = useState('');
  const [previewLoading, setPreviewLoading] = useState(false);

  // Delete confirmation state
  const [deleteConfirmOpen, setDeleteConfirmOpen] = useState(false);
  const [templateToDelete, setTemplateToDelete] = useState<EmailTemplate | null>(null);

  // Restore confirmation state
  const [restoreConfirmOpen, setRestoreConfirmOpen] = useState(false);
  const [versionToRestore, setVersionToRestore] = useState<VersionInfo | null>(null);
  const [restoring, setRestoring] = useState(false);

  // Layout editor state
  const [layoutFormData, setLayoutFormData] = useState({
    baseLayout: '',
    header: '',
    footer: ''
  });
  const [savingLayout, setSavingLayout] = useState(false);

  // Capitol Commons logo state
  const [logoUrl, setLogoUrl] = useState<string | null>(null);
  const [logoLoading, setLogoLoading] = useState(false);
  const [uploadingLogo, setUploadingLogo] = useState(false);
  const logoInputRef = React.useRef<HTMLInputElement>(null);

  // Track whether we've attempted fetches (to prevent infinite loops on error)
  const [partialsFetched, setPartialsFetched] = useState(false);
  const [logoFetched, setLogoFetched] = useState(false);

  // Copy to clipboard state
  const [copiedPlaceholder, setCopiedPlaceholder] = useState<string | null>(null);

  // Fetch templates
  const fetchTemplates = useCallback(async () => {
    try {
      setTemplatesLoading(true);
      const response = await api.get('/admin/email-templates');
      setTemplates(response.data.templates || []);
    } catch (error) {
      console.error('Failed to fetch templates:', error);
      toast.error('Failed to load email templates');
    } finally {
      setTemplatesLoading(false);
    }
  }, []);

  // Fetch partials
  const fetchPartials = useCallback(async () => {
    try {
      setPartialsLoading(true);
      const response = await api.get('/admin/email-templates/partials');
      setPartials({
        baseLayout: response.data.baseLayout,
        partials: response.data.partials
      });
      setLayoutFormData({
        baseLayout: response.data.baseLayout || '',
        header: response.data.partials?.header || '',
        footer: response.data.partials?.footer || ''
      });
    } catch (error) {
      console.error('Failed to fetch partials:', error);
      toast.error('Failed to load email layout');
    } finally {
      setPartialsLoading(false);
      setPartialsFetched(true);
    }
  }, []);

  // Fetch versions
  const fetchVersions = useCallback(async () => {
    try {
      setVersionsLoading(true);
      const response = await api.get('/admin/email-templates/versions');
      setVersions(response.data.versions || []);
    } catch (error) {
      console.error('Failed to fetch versions:', error);
      toast.error('Failed to load version history');
    } finally {
      setVersionsLoading(false);
    }
  }, []);

  // Fetch Capitol Commons logo
  const fetchLogo = useCallback(async () => {
    try {
      setLogoLoading(true);
      const response = await api.get('/admin/email-templates/capitol-commons-logo');
      setLogoUrl(response.data.logoUrl || null);
    } catch (error) {
      console.error('Failed to fetch logo:', error);
      // Don't show error toast - logo might not be uploaded yet
      setLogoUrl(null);
    } finally {
      setLogoLoading(false);
      setLogoFetched(true);
    }
  }, []);

  // Upload Capitol Commons logo
  const handleLogoUpload = async (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (!file) return;

    // Validate file type
    const allowedTypes = ['image/jpeg', 'image/png', 'image/svg+xml', 'image/webp'];
    if (!allowedTypes.includes(file.type)) {
      toast.error('Invalid file type. Only JPEG, PNG, SVG, and WebP are allowed.');
      return;
    }

    // Validate file size (2MB)
    if (file.size > 2 * 1024 * 1024) {
      toast.error('File too large. Maximum size is 2MB.');
      return;
    }

    try {
      setUploadingLogo(true);
      const formData = new FormData();
      formData.append('logo', file);

      const response = await api.post('/admin/email-templates/capitol-commons-logo', formData, {
        headers: {
          'Content-Type': 'multipart/form-data',
        },
      });

      setLogoUrl(response.data.logoUrl);
      setLogoFetched(true); // Mark as fetched since we have new data
      toast.success('Logo uploaded successfully');
    } catch (error: unknown) {
      const err = error as { response?: { data?: { error?: string } } };
      console.error('Failed to upload logo:', error);
      toast.error(err.response?.data?.error || 'Failed to upload logo');
    } finally {
      setUploadingLogo(false);
      // Reset the input
      if (logoInputRef.current) {
        logoInputRef.current.value = '';
      }
    }
  };

  // Initial fetch
  useEffect(() => {
    fetchTemplates();
  }, [fetchTemplates]);

  // Fetch data when tab changes
  useEffect(() => {
    if (activeTab === 'layout') {
      if (!partialsFetched && !partialsLoading) {
        fetchPartials();
      }
      if (!logoFetched && !logoLoading) {
        fetchLogo();
      }
    }
    if (activeTab === 'versions') {
      fetchVersions();
    }
  }, [activeTab, partialsFetched, partialsLoading, logoFetched, logoLoading, fetchPartials, fetchVersions, fetchLogo]);

  // Filter templates
  const filteredTemplates = templates.filter(template =>
    template.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
    template.id.toLowerCase().includes(searchTerm.toLowerCase()) ||
    template.subject.toLowerCase().includes(searchTerm.toLowerCase())
  );

  // Generate ID from name
  const generateId = (name: string) => {
    return name
      .toLowerCase()
      .replace(/[^a-z0-9\s-]/g, '')
      .replace(/\s+/g, '-')
      .replace(/-+/g, '-')
      .trim();
  };

  // Open editor for new template
  const handleCreateTemplate = () => {
    setEditorMode('create');
    setEditingTemplate(null);
    setFormData({
      id: '',
      name: '',
      subject: '',
      htmlContent: '<h1>Email Title</h1>\n<p>Hi {{userName}},</p>\n<p>Your email content here.</p>',
      useBaseLayout: true,
      placeholders: ['userName'],
      sampleData: { userName: 'John Doe' }
    });
    setPlaceholderInput('');
    setIsEditorOpen(true);
  };

  // Open editor for editing template
  const handleEditTemplate = (template: EmailTemplate) => {
    setEditorMode('edit');
    setEditingTemplate(template);
    setFormData({
      id: template.id,
      name: template.name,
      subject: template.subject,
      htmlContent: template.htmlContent,
      useBaseLayout: template.useBaseLayout,
      placeholders: template.placeholders || [],
      sampleData: template.sampleData || {}
    });
    setPlaceholderInput('');
    setIsEditorOpen(true);
  };

  // Add placeholder
  const handleAddPlaceholder = () => {
    const placeholder = placeholderInput.trim().replace(/[^a-zA-Z0-9_]/g, '');
    if (placeholder && !formData.placeholders.includes(placeholder)) {
      setFormData(prev => ({
        ...prev,
        placeholders: [...prev.placeholders, placeholder],
        sampleData: { ...prev.sampleData, [placeholder]: `Sample ${placeholder}` }
      }));
      setPlaceholderInput('');
    }
  };

  // Remove placeholder
  const handleRemovePlaceholder = (placeholder: string) => {
    setFormData(prev => {
      const newSampleData = { ...prev.sampleData };
      delete newSampleData[placeholder];
      return {
        ...prev,
        placeholders: prev.placeholders.filter(p => p !== placeholder),
        sampleData: newSampleData
      };
    });
  };

  // Update sample data
  const handleUpdateSampleData = (placeholder: string, value: string) => {
    setFormData(prev => ({
      ...prev,
      sampleData: { ...prev.sampleData, [placeholder]: value }
    }));
  };

  // Copy placeholder to clipboard
  const handleCopyPlaceholder = (placeholder: string) => {
    navigator.clipboard.writeText(`{{${placeholder}}}`);
    setCopiedPlaceholder(placeholder);
    setTimeout(() => setCopiedPlaceholder(null), 2000);
  };

  // Save template
  const handleSaveTemplate = async () => {
    if (!formData.name || !formData.subject || !formData.htmlContent) {
      toast.error('Please fill in all required fields');
      return;
    }

    try {
      setSaving(true);
      if (editorMode === 'create') {
        const id = formData.id || generateId(formData.name);
        await api.post('/admin/email-templates', {
          id,
          name: formData.name,
          subject: formData.subject,
          htmlContent: formData.htmlContent,
          useBaseLayout: formData.useBaseLayout,
          placeholders: formData.placeholders,
          sampleData: formData.sampleData
        });
        toast.success('Template created successfully');
      } else {
        await api.put(`/admin/email-templates/${editingTemplate?.id}`, {
          name: formData.name,
          subject: formData.subject,
          htmlContent: formData.htmlContent,
          useBaseLayout: formData.useBaseLayout,
          placeholders: formData.placeholders,
          sampleData: formData.sampleData
        });
        toast.success('Template updated successfully');
      }
      setIsEditorOpen(false);
      fetchTemplates();
    } catch (error: unknown) {
      const err = error as { response?: { data?: { error?: string } } };
      console.error('Failed to save template:', error);
      toast.error(err.response?.data?.error || 'Failed to save template');
    } finally {
      setSaving(false);
    }
  };

  // Delete template
  const handleDeleteTemplate = async () => {
    if (!templateToDelete) return;

    try {
      await api.delete(`/admin/email-templates/${templateToDelete.id}`);
      toast.success('Template deleted successfully');
      setDeleteConfirmOpen(false);
      setTemplateToDelete(null);
      fetchTemplates();
    } catch (error) {
      console.error('Failed to delete template:', error);
      toast.error('Failed to delete template');
    }
  };

  // Preview template
  const handlePreview = async (template: EmailTemplate) => {
    try {
      setPreviewLoading(true);
      setShowPreview(true);
      const response = await api.post(`/admin/email-templates/${template.id}/preview`, {
        sampleData: template.sampleData
      });
      setPreviewHtml(response.data.preview.html);
      setPreviewSubject(response.data.preview.subject);
    } catch (error) {
      console.error('Failed to render preview:', error);
      toast.error('Failed to render preview');
      setShowPreview(false);
    } finally {
      setPreviewLoading(false);
    }
  };

  // Preview from editor
  const handlePreviewFromEditor = async () => {
    if (!formData.id && !editingTemplate?.id) {
      // For new templates, create a temporary preview
      const subject = formData.subject.replace(/{{\s*([\w.]+)\s*}}/g, (_, key) => {
        return formData.sampleData[key] || '';
      });
      
      // Simple preview without base layout for new templates
      let html = formData.htmlContent;
      Object.entries(formData.sampleData).forEach(([key, value]) => {
        html = html.replace(new RegExp(`{{\\s*${key}\\s*}}`, 'g'), value);
      });
      
      setPreviewHtml(html);
      setPreviewSubject(subject);
      setShowPreview(true);
      return;
    }

    try {
      setPreviewLoading(true);
      setShowPreview(true);
      
      // Save temp changes then preview
      const templateId = editingTemplate?.id || formData.id;
      const response = await api.post(`/admin/email-templates/${templateId}/preview`, {
        sampleData: formData.sampleData
      });
      setPreviewHtml(response.data.preview.html);
      setPreviewSubject(response.data.preview.subject);
    } catch (error) {
      // Fallback to local preview
      const subject = formData.subject.replace(/{{\s*([\w.]+)\s*}}/g, (_, key) => {
        return formData.sampleData[key] || '';
      });
      
      let html = formData.htmlContent;
      Object.entries(formData.sampleData).forEach(([key, value]) => {
        html = html.replace(new RegExp(`{{\\s*${key}\\s*}}`, 'g'), value);
      });
      
      setPreviewHtml(html);
      setPreviewSubject(subject);
    } finally {
      setPreviewLoading(false);
    }
  };

  // Save layout
  const handleSaveLayout = async () => {
    try {
      setSavingLayout(true);
      await api.put('/admin/email-templates/partials', {
        baseLayout: layoutFormData.baseLayout,
        partials: {
          header: layoutFormData.header,
          footer: layoutFormData.footer
        }
      });
      toast.success('Layout saved successfully');
      fetchPartials();
    } catch (error) {
      console.error('Failed to save layout:', error);
      toast.error('Failed to save layout');
    } finally {
      setSavingLayout(false);
    }
  };

  // Restore version
  const handleRestoreVersion = async () => {
    if (!versionToRestore) return;

    try {
      setRestoring(true);
      await api.post(`/admin/email-templates/versions/${versionToRestore.id}/restore`);
      toast.success('Version restored successfully');
      setRestoreConfirmOpen(false);
      setVersionToRestore(null);
      fetchTemplates();
      fetchVersions();
      if (partials) {
        fetchPartials();
      }
    } catch (error) {
      console.error('Failed to restore version:', error);
      toast.error('Failed to restore version');
    } finally {
      setRestoring(false);
    }
  };

  // Migration
  const handleMigrate = async () => {
    try {
      const response = await api.post('/admin/email-templates/migrate');
      toast.success(`Migration successful! ${response.data.stats.templates} templates migrated.`);
      fetchTemplates();
    } catch (error) {
      console.error('Migration failed:', error);
      toast.error('Migration failed');
    }
  };

  // Export
  const handleExport = async () => {
    try {
      const response = await api.get('/admin/email-templates/export');
      const blob = new Blob([JSON.stringify(response.data, null, 2)], { type: 'application/json' });
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = 'email-templates.json';
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      URL.revokeObjectURL(url);
      toast.success('Templates exported successfully');
    } catch (error) {
      console.error('Export failed:', error);
      toast.error('Failed to export templates');
    }
  };

  // Format timestamp for display
  const formatTimestamp = (timestamp: string) => {
    try {
      const date = new Date(timestamp);
      return date.toLocaleString('en-US', {
        year: 'numeric',
        month: 'short',
        day: 'numeric',
        hour: '2-digit',
        minute: '2-digit'
      });
    } catch {
      return timestamp;
    }
  };

  return (
    <div className="space-y-6">
      {/* Action Buttons */}
      <div className="flex justify-end gap-2">
        <Button variant="secondary" onClick={handleExport}>
          <Download className="w-4 h-4 mr-2" />
          Export
        </Button>
        <Button variant="secondary" onClick={handleMigrate}>
          <Upload className="w-4 h-4 mr-2" />
          Migrate from Files
        </Button>
      </div>

      {/* Tabs */}
      <Tabs
        level="panel"
        ariaLabel="Email template views"
        tabs={[
          {
            id: 'templates',
            label: 'Templates',
            icon: Mail,
            badge: <span className="tabular-nums">({templates.length})</span>,
          },
          { id: 'layout', label: 'Base Layout', icon: Layout },
          { id: 'versions', label: 'Version History', icon: History },
        ]}
        activeId={activeTab}
        onChange={(id) => setActiveTab(id as typeof activeTab)}
      />

      {/* Templates Tab */}
      {activeTab === 'templates' && (
        <div className="space-y-4">
          {/* Toolbar */}
          <div className="flex flex-col sm:flex-row gap-4 items-start sm:items-center justify-between">
            <SearchField
              value={searchTerm}
              onChange={setSearchTerm}
              placeholder="Search templates..."
              className="w-full sm:w-64"
            />
            <Button onClick={handleCreateTemplate} variant="primary">
              <Plus className="w-4 h-4 mr-2" />
              Add Template
            </Button>
          </div>

          {/* Templates Table */}
          {templatesLoading ? (
            <div className="flex items-center justify-center py-12">
              <Loader2 className="w-8 h-8 animate-spin text-primary-500" />
            </div>
          ) : filteredTemplates.length === 0 ? (
            <div className="card text-center py-12">
              <Mail className="w-12 h-12 mx-auto text-text-tertiary dark:text-text-dark-tertiary mb-4" />
              <h3 className="text-lg font-medium text-text-primary dark:text-text-dark-primary mb-2">
                No templates found
              </h3>
              <p className="text-text-secondary dark:text-text-dark-secondary mb-4">
                {searchTerm 
                  ? 'Try adjusting your search' 
                  : 'Create your first email template or migrate from local files'}
              </p>
              {!searchTerm && (
                <Button onClick={handleMigrate} variant="secondary">
                  <Upload className="w-4 h-4 mr-2" />
                  Migrate from Local Files
                </Button>
              )}
            </div>
          ) : (
            <div className="card p-0 overflow-hidden">
              <StandardTable>
                <StandardTableHeader>
                  <tr>
                    <StandardTableHeaderCell>Template</StandardTableHeaderCell>
                    <StandardTableHeaderCell>Subject</StandardTableHeaderCell>
                    <StandardTableHeaderCell>Layout</StandardTableHeaderCell>
                    <StandardTableHeaderCell className="text-right">
                      <span className="inline-flex items-center gap-1.5">
                        Actions
                        <ActionsHeaderHelp icons={['preview', 'edit', 'delete']} />
                      </span>
                    </StandardTableHeaderCell>
                  </tr>
                </StandardTableHeader>
                <StandardTableBody>
                  {filteredTemplates.map(template => (
                    <StandardTableRow key={template.id}>
                      <StandardTableCell>
                        <div className="flex items-start gap-3">
                          <div className="p-2 rounded-lg bg-primary-50 dark:bg-primary-900/20">
                            <Mail className="w-5 h-5 text-primary-600 dark:text-primary-400" />
                          </div>
                          <div>
                            <div className="font-medium text-text-primary dark:text-text-dark-primary">
                              {template.name}
                            </div>
                            <div className="text-xs text-text-tertiary dark:text-text-dark-tertiary mt-1 font-mono">
                              {template.id}
                            </div>
                          </div>
                        </div>
                      </StandardTableCell>
                      <StandardTableCell>
                        <span className="text-sm text-text-secondary dark:text-text-dark-secondary">
                          {template.subject}
                        </span>
                      </StandardTableCell>
                      <StandardTableCell>
                        <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${
                          template.useBaseLayout
                            ? 'bg-green-50 dark:bg-green-900/20 text-green-700 dark:text-green-300'
                            : 'bg-background dark:bg-background-dark-primary/20 text-neutral-700 dark:text-text-dark-secondary'
                        }`}>
                          {template.useBaseLayout ? 'Uses Base Layout' : 'Standalone'}
                        </span>
                      </StandardTableCell>
                      <StandardTableCell className="text-right">
                        <div className="flex items-center justify-end gap-2">
                          <button
                            onClick={() => handlePreview(template)}
                            className="p-2 text-text-secondary dark:text-text-dark-secondary hover:text-text-primary dark:hover:text-text-dark-primary hover:bg-background-tertiary dark:hover:bg-background-dark-secondary rounded-lg transition-colors"
                            title="Preview"
                            aria-label={`Preview ${template.name}`}
                          >
                            <Eye className="w-4 h-4" />
                          </button>
                          <button
                            onClick={() => handleEditTemplate(template)}
                            className="p-2 text-text-secondary dark:text-text-dark-secondary hover:text-primary-600 dark:hover:text-primary-400 hover:bg-primary-50 dark:hover:bg-primary-900/20 rounded-lg transition-colors"
                            title="Edit"
                            aria-label={`Edit ${template.name}`}
                          >
                            <EditIcon className="w-4 h-4" />
                          </button>
                          <button
                            onClick={() => {
                              setTemplateToDelete(template);
                              setDeleteConfirmOpen(true);
                            }}
                            className="p-2 text-text-secondary dark:text-text-dark-secondary hover:text-red-600 dark:hover:text-red-400 hover:bg-red-50 dark:hover:bg-red-900/20 rounded-lg transition-colors"
                            title="Delete"
                            aria-label={`Delete ${template.name}`}
                          >
                            <Trash2 className="w-4 h-4" />
                          </button>
                        </div>
                      </StandardTableCell>
                    </StandardTableRow>
                  ))}
                </StandardTableBody>
              </StandardTable>
            </div>
          )}
        </div>
      )}

      {/* Base Layout Tab */}
      {activeTab === 'layout' && (
        <div className="space-y-6">
          {/* Info banner */}
          <div className="bg-blue-50 dark:bg-blue-900/20 border border-blue-200 dark:border-blue-800 rounded-lg p-4">
            <p className="text-sm text-blue-700 dark:text-blue-300">
              <Layout className="w-4 h-4 inline-block mr-2" />
              The base layout wraps all email templates that have &quot;Use Base Layout&quot; enabled.
              Edit the header, footer, and main layout structure here.
            </p>
          </div>

          {partialsLoading ? (
            <div className="flex items-center justify-center py-12">
              <Loader2 className="w-8 h-8 animate-spin text-primary-500" />
            </div>
          ) : (
            <div className="space-y-6">
              {/* Capitol Commons Logo */}
              <div className="card">
                <div className="flex items-center justify-between mb-4">
                  <div>
                    <h3 className="text-lg font-semibold text-text-primary dark:text-text-dark-primary">
                      Capitol Commons Logo
                    </h3>
                    <p className="text-sm text-text-secondary dark:text-text-dark-secondary mt-1">
                      This logo appears in the footer of all emails. Stored at: <code className="bg-background-tertiary dark:bg-background-dark-tertiary px-1 rounded text-xs">system/capitol-commons-logo.png</code>
                    </p>
                  </div>
                </div>

                <div className="flex items-center gap-6">
                  {/* Logo Preview */}
                  <div className="flex-shrink-0">
                    {logoLoading ? (
                      <div className="w-32 h-16 flex items-center justify-center bg-background-tertiary dark:bg-background-dark-tertiary rounded-lg">
                        <Loader2 className="w-6 h-6 animate-spin text-text-tertiary" />
                      </div>
                    ) : logoUrl ? (
                      <div className="p-4 bg-background-tertiary dark:bg-background-dark-tertiary rounded-lg">
                        <img 
                          src={logoUrl} 
                          alt="Capitol Commons Logo" 
                          className="h-12 w-auto object-contain"
                        />
                      </div>
                    ) : (
                      <div className="w-32 h-16 flex flex-col items-center justify-center bg-background-tertiary dark:bg-background-dark-tertiary rounded-lg text-text-tertiary dark:text-text-dark-tertiary">
                        <Image className="w-8 h-8 mb-1" />
                        <span className="text-xs">No logo</span>
                      </div>
                    )}
                  </div>

                  {/* Upload Button */}
                  <div className="flex-1">
                    <input
                      ref={logoInputRef}
                      type="file"
                      accept="image/jpeg,image/png,image/svg+xml,image/webp"
                      onChange={handleLogoUpload}
                      className="hidden"
                      id="logo-upload"
                    />
                    <Button
                      variant="secondary"
                      onClick={() => logoInputRef.current?.click()}
                      disabled={uploadingLogo}
                    >
                      {uploadingLogo ? (
                        <>
                          <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                          Uploading...
                        </>
                      ) : (
                        <>
                          <Upload className="w-4 h-4 mr-2" />
                          {logoUrl ? 'Replace Logo' : 'Upload Logo'}
                        </>
                      )}
                    </Button>
                    <p className="text-xs text-text-tertiary dark:text-text-dark-tertiary mt-2">
                      Accepted formats: JPEG, PNG, SVG, WebP. Max size: 2MB.
                    </p>
                  </div>
                </div>
              </div>

              {/* Base Layout Editor */}
              <div className="card">
                <h3 className="text-lg font-semibold text-text-primary dark:text-text-dark-primary mb-4">
                  Base Layout HTML
                </h3>
                <p className="text-sm text-text-secondary dark:text-text-dark-secondary mb-4">
                  Use <code className="bg-background-tertiary dark:bg-background-dark-tertiary px-1 rounded">{'{{headerHtml}}'}</code>,
                  <code className="bg-background-tertiary dark:bg-background-dark-tertiary px-1 rounded ml-1">{'{{contentHtml}}'}</code>, and
                  <code className="bg-background-tertiary dark:bg-background-dark-tertiary px-1 rounded ml-1">{'{{footerHtml}}'}</code> as placeholders.
                </p>
                <textarea
                  value={layoutFormData.baseLayout}
                  onChange={(e) => setLayoutFormData(prev => ({ ...prev, baseLayout: e.target.value }))}
                  className="input w-full h-64 font-mono text-sm resize-none"
                  placeholder="<!DOCTYPE html>..."
                />
              </div>

              {/* Header Partial */}
              <div className="card">
                <h3 className="text-lg font-semibold text-text-primary dark:text-text-dark-primary mb-4">
                  Header Partial
                </h3>
                <textarea
                  value={layoutFormData.header}
                  onChange={(e) => setLayoutFormData(prev => ({ ...prev, header: e.target.value }))}
                  className="input w-full h-32 font-mono text-sm resize-none"
                  placeholder="<table>..."
                />
              </div>

              {/* Footer Partial */}
              <div className="card">
                <h3 className="text-lg font-semibold text-text-primary dark:text-text-dark-primary mb-4">
                  Footer Partial
                </h3>
                <textarea
                  value={layoutFormData.footer}
                  onChange={(e) => setLayoutFormData(prev => ({ ...prev, footer: e.target.value }))}
                  className="input w-full h-32 font-mono text-sm resize-none"
                  placeholder="<div>..."
                />
              </div>

              {/* Save Button */}
              <div className="flex justify-end">
                <Button
                  variant="primary"
                  onClick={handleSaveLayout}
                  disabled={savingLayout}
                >
                  {savingLayout ? (
                    <>
                      <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                      Saving...
                    </>
                  ) : (
                    <>
                      <Save className="w-4 h-4 mr-2" />
                      Save Layout
                    </>
                  )}
                </Button>
              </div>
            </div>
          )}
        </div>
      )}

      {/* Version History Tab */}
      {activeTab === 'versions' && (
        <div className="space-y-4">
          {/* Info banner */}
          <div className="bg-amber-50 dark:bg-amber-900/20 border border-amber-200 dark:border-amber-800 rounded-lg p-4">
            <div className="flex gap-3">
              <AlertCircle className="w-5 h-5 text-amber-600 dark:text-amber-400 flex-shrink-0 mt-0.5" />
              <div className="text-sm text-amber-700 dark:text-amber-300">
                <p className="font-medium mb-1">Version History</p>
                <p>Each time templates are saved, a version snapshot is created. You can restore to any previous version.</p>
              </div>
            </div>
          </div>

          {versionsLoading ? (
            <div className="flex items-center justify-center py-12">
              <Loader2 className="w-8 h-8 animate-spin text-primary-500" />
            </div>
          ) : versions.length === 0 ? (
            <div className="card text-center py-12">
              <History className="w-12 h-12 mx-auto text-text-tertiary dark:text-text-dark-tertiary mb-4" />
              <h3 className="text-lg font-medium text-text-primary dark:text-text-dark-primary mb-2">
                No version history
              </h3>
              <p className="text-text-secondary dark:text-text-dark-secondary">
                Version snapshots are created each time templates are saved.
              </p>
            </div>
          ) : (
            <div className="card p-0 overflow-hidden">
              <StandardTable>
                <StandardTableHeader>
                  <tr>
                    <StandardTableHeaderCell>Version</StandardTableHeaderCell>
                    <StandardTableHeaderCell>Date</StandardTableHeaderCell>
                    <StandardTableHeaderCell className="text-right">Actions</StandardTableHeaderCell>
                  </tr>
                </StandardTableHeader>
                <StandardTableBody>
                  {versions.map((version, index) => (
                    <StandardTableRow key={version.id}>
                      <StandardTableCell>
                        <div className="flex items-center gap-2">
                          <History className="w-4 h-4 text-text-tertiary dark:text-text-dark-tertiary" />
                          <code className="text-sm font-mono bg-background-tertiary dark:bg-background-dark-tertiary px-2 py-0.5 rounded">
                            {version.id}
                          </code>
                          {index === 0 && (
                            <span className="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium bg-primary-50 dark:bg-primary-900/20 text-primary-700 dark:text-primary-300">
                              Latest
                            </span>
                          )}
                        </div>
                      </StandardTableCell>
                      <StandardTableCell>
                        <span className="text-text-secondary dark:text-text-dark-secondary">
                          {formatTimestamp(version.timestamp)}
                        </span>
                      </StandardTableCell>
                      <StandardTableCell className="text-right">
                        <button
                          onClick={() => {
                            setVersionToRestore(version);
                            setRestoreConfirmOpen(true);
                          }}
                          className="inline-flex items-center gap-1 px-3 py-1.5 text-sm text-primary-600 dark:text-primary-400 hover:bg-primary-50 dark:hover:bg-primary-900/20 rounded-lg transition-colors"
                          disabled={index === 0}
                          title={index === 0 ? 'This is the current version' : 'Restore this version'}
                        >
                          <RotateCcw className="w-4 h-4" />
                          Restore
                        </button>
                      </StandardTableCell>
                    </StandardTableRow>
                  ))}
                </StandardTableBody>
              </StandardTable>
            </div>
          )}
        </div>
      )}

      {/* Editor Modal */}
      {isEditorOpen && (
        <Modal
          isOpen={true}
          title={editorMode === 'create' ? 'Create template' : 'Edit template'}
          onClose={() => setIsEditorOpen(false)}
          size="xl"
          className="sm:max-w-7xl"
          footer={
            <>
              {/* Preview sits left: it opens another dialog rather than resolving
                  this one, so it should not read as a peer of Cancel/Save. */}
              <Button variant="secondary" icon={Eye} onClick={handlePreviewFromEditor}>
                Preview
              </Button>
              <span className="flex-1" />
              <Button variant="secondary" onClick={() => setIsEditorOpen(false)}>
                Cancel
              </Button>
              <Button variant="primary" icon={Save} onClick={handleSaveTemplate} loading={saving} disabled={saving}>
                Save template
              </Button>
            </>
          }
        >
                <div className="space-y-6">
                  {/* Template metadata */}
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div>
                      <label className="block text-sm font-medium text-text-secondary dark:text-text-dark-secondary mb-2">
                        Template Name <span className="text-red-500">*</span>
                      </label>
                      <input
                        type="text"
                        value={formData.name}
                        onChange={(e) => {
                          setFormData(prev => ({
                            ...prev,
                            name: e.target.value,
                            ...(editorMode === 'create' ? { id: generateId(e.target.value) } : {})
                          }));
                        }}
                        className="input w-full"
                        placeholder="e.g., Password Reset"
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-text-secondary dark:text-text-dark-secondary mb-2">
                        Template ID <span className="text-red-500">*</span>
                      </label>
                      <input
                        type="text"
                        value={formData.id}
                        onChange={(e) => setFormData(prev => ({ ...prev, id: e.target.value }))}
                        className="input w-full font-mono text-sm"
                        placeholder="password-reset"
                        disabled={editorMode === 'edit'}
                      />
                    </div>
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-text-secondary dark:text-text-dark-secondary mb-2">
                      Subject Line <span className="text-red-500">*</span>
                    </label>
                    <input
                      type="text"
                      value={formData.subject}
                      onChange={(e) => setFormData(prev => ({ ...prev, subject: e.target.value }))}
                      className="input w-full"
                      placeholder="e.g., Reset your {{appName}} password"
                    />
                    <p className="text-xs text-text-tertiary dark:text-text-dark-tertiary mt-1">
                      Use {'{{placeholder}}'} syntax for dynamic values
                    </p>
                  </div>

                  <div className="flex items-center gap-2">
                    <input
                      type="checkbox"
                      id="useBaseLayout"
                      checked={formData.useBaseLayout}
                      onChange={(e) => setFormData(prev => ({ ...prev, useBaseLayout: e.target.checked }))}
                      className="h-4 w-4 text-primary-600 focus:ring-primary-500 border-border-light dark:border-border-dark rounded"
                    />
                    <label htmlFor="useBaseLayout" className="text-sm text-text-secondary dark:text-text-dark-secondary">
                      Use base layout (header/footer wrapper)
                    </label>
                  </div>

                  {/* Placeholders */}
                  <div>
                    <label className="block text-sm font-medium text-text-secondary dark:text-text-dark-secondary mb-2">
                      Placeholders
                    </label>
                    <div className="flex gap-2 mb-2">
                      <input
                        type="text"
                        value={placeholderInput}
                        onChange={(e) => setPlaceholderInput(e.target.value)}
                        onKeyDown={(e) => e.key === 'Enter' && (e.preventDefault(), handleAddPlaceholder())}
                        className="input flex-1"
                        placeholder="Add placeholder name..."
                      />
                      <Button variant="secondary" onClick={handleAddPlaceholder}>
                        <Plus className="w-4 h-4" />
                      </Button>
                    </div>
                    {formData.placeholders.length > 0 && (
                      <div className="flex flex-wrap gap-2">
                        {formData.placeholders.map(placeholder => (
                          <div
                            key={placeholder}
                            className="inline-flex items-center gap-1 px-2 py-1 bg-background-tertiary dark:bg-background-dark-tertiary rounded-md text-sm"
                          >
                            <button
                              onClick={() => handleCopyPlaceholder(placeholder)}
                              className="text-text-secondary dark:text-text-dark-secondary hover:text-primary-600 dark:hover:text-primary-400"
                              title="Copy placeholder syntax"
                            >
                              {copiedPlaceholder === placeholder ? (
                                <Check className="w-3 h-3 text-green-500" />
                              ) : (
                                <Copy className="w-3 h-3" />
                              )}
                            </button>
                            <code className="font-mono text-text-primary dark:text-text-dark-primary">
                              {`{{${placeholder}}}`}
                            </code>
                            <button
                              onClick={() => handleRemovePlaceholder(placeholder)}
                              className="text-text-tertiary dark:text-text-dark-tertiary hover:text-red-500"
                            >
                              <X className="w-3 h-3" />
                            </button>
                          </div>
                        ))}
                      </div>
                    )}
                  </div>

                  {/* Sample Data */}
                  {formData.placeholders.length > 0 && (
                    <div>
                      <label className="block text-sm font-medium text-text-secondary dark:text-text-dark-secondary mb-2">
                        Sample Data (for preview)
                      </label>
                      <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                        {formData.placeholders.map(placeholder => (
                          <div key={placeholder}>
                            <label className="block text-xs text-text-tertiary dark:text-text-dark-tertiary mb-1 font-mono">
                              {placeholder}
                            </label>
                            <input
                              type="text"
                              value={formData.sampleData[placeholder] || ''}
                              onChange={(e) => handleUpdateSampleData(placeholder, e.target.value)}
                              className="input w-full text-sm"
                              placeholder={`Sample value for ${placeholder}`}
                            />
                          </div>
                        ))}
                      </div>
                    </div>
                  )}

                  {/* HTML Content Editor with Side-by-Side Preview */}
                  <div>
                    <label className="block text-sm font-medium text-text-secondary dark:text-text-dark-secondary mb-2">
                      HTML Content <span className="text-red-500">*</span>
                    </label>
                    <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
                      <div>
                        <div className="flex items-center justify-end mb-2">
                          <div className="flex items-center gap-1 text-xs text-text-tertiary dark:text-text-dark-tertiary">
                            <Code className="w-3 h-3" />
                            HTML Editor
                          </div>
                        </div>
                        <textarea
                          value={formData.htmlContent}
                          onChange={(e) => setFormData(prev => ({ ...prev, htmlContent: e.target.value }))}
                          className="input w-full h-96 font-mono text-sm resize-none"
                          placeholder="<h1>Email Title</h1>\n<p>Hi {{userName}},</p>..."
                        />
                      </div>
                      <div className="border border-border-light dark:border-border-dark rounded-lg overflow-hidden h-96 flex flex-col">
                        <div className="px-4 py-2 bg-white dark:bg-background-dark-secondary border-b border-border-light dark:border-border-dark">
                          <div className="text-xs font-medium text-text-tertiary dark:text-text-dark-tertiary uppercase tracking-wide">
                            Preview
                          </div>
                        </div>
                        <div className="flex-1 overflow-y-auto bg-white">
                          {formData.htmlContent ? (
                            <div 
                              className="p-4"
                              dangerouslySetInnerHTML={{ 
                                __html: (() => {
                                  let html = formData.htmlContent;
                                  Object.entries(formData.sampleData).forEach(([key, value]) => {
                                    html = html.replace(new RegExp(`{{\\s*${key}\\s*}}`, 'g'), value);
                                  });
                                  return html;
                                })()
                              }}
                            />
                          ) : (
                            <p className="p-4 text-text-tertiary dark:text-text-dark-tertiary italic">
                              Start typing to see preview...
                            </p>
                          )}
                        </div>
                      </div>
                    </div>
                  </div>
                </div>

        </Modal>
      )}

      {/* Preview Modal */}
      {showPreview && (
        <Modal
          isOpen={true}
          title="Email preview"
          description={previewSubject ? `Subject: ${previewSubject}` : undefined}
          onClose={() => setShowPreview(false)}
          size="xl"
          footer={
            <Button variant="secondary" onClick={() => setShowPreview(false)}>
              Close
            </Button>
          }
        >
                {previewLoading ? (
                  <div className="flex items-center justify-center py-12">
                    <Loader2 className="w-8 h-8 animate-spin text-primary-500" />
                  </div>
                ) : (
                  <div 
                    className="bg-white rounded-lg shadow-lg overflow-hidden"
                    dangerouslySetInnerHTML={{ __html: previewHtml }}
                  />
                )}
        </Modal>
      )}

      {/* Delete Confirmation Dialog */}
      <ConfirmationDialog
        isOpen={deleteConfirmOpen}
        onCancel={() => {
          setDeleteConfirmOpen(false);
          setTemplateToDelete(null);
        }}
        onConfirm={handleDeleteTemplate}
        title="Delete Template"
        message={`Are you sure you want to delete "${templateToDelete?.name}"? This action cannot be undone.`}
        confirmText="Delete"
        variant="danger"
      />

      {/* Restore Confirmation Dialog */}
      <ConfirmationDialog
        isOpen={restoreConfirmOpen}
        onCancel={() => {
          setRestoreConfirmOpen(false);
          setVersionToRestore(null);
        }}
        onConfirm={handleRestoreVersion}
        title="Restore Version"
        message={`Are you sure you want to restore to version ${versionToRestore?.id}? This will replace all current templates with the version from ${versionToRestore ? formatTimestamp(versionToRestore.timestamp) : ''}.`}
        confirmText={restoring ? 'Restoring...' : 'Restore'}
        variant="warning"
      />
    </div>
  );
};

export default EmailTemplatesAdminPage;

